import { AsyncPipe } from '@angular/common';
import { Component, inject, input, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { DateAdapter, MatOption } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatError, MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { UnsubscribeService } from '@webgate/angular-azure-core';
import { Audit } from 'src/app/models/audit/audit';
import { Auditor } from 'src/app/models/auditor';
import { Organization } from 'src/app/models/organization';
import { Plant } from 'src/app/models/plant';
import { AuditService } from 'src/app/services/audit.service';
import { PlantService } from 'src/app/services/plant.service';
import { AccessService } from 'src/app/services/access.service';

import { UserNamePipe } from '../../../pipes/user-name.pipe';
import { AzureIdStatusPipe } from '../../../pipes/azure-id-status.pipe';
import { PersonSelectComponent } from '../../person-select-dialog/person-select.component';

@Component({
  selector: 'app-audit-basics',
  templateUrl: './audit-basics.component.html',
  styleUrls: ['./audit-basics.component.scss'],
  imports: [
    FormsModule,
    MatIconModule,
    ReactiveFormsModule,
    AsyncPipe,
    MatCard,
    MatCardTitle,
    MatCardContent,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatError,
    MatCheckbox,
    MatSelect,
    MatOption,
    MatCardHeader,
    MatRadioGroup,
    MatRadioButton,
    MatButton,
    UserNamePipe,
    AzureIdStatusPipe
  ]
})
export class AuditBasicsComponent implements OnInit {
  private _adapter = inject<DateAdapter<Date>>(DateAdapter);
  private auditService = inject(AuditService);
  private router = inject(Router);
  readonly accessService = inject(AccessService);
  private snackBar = inject(MatSnackBar);
  plantService = inject(PlantService);

  readonly form = input.required<UntypedFormGroup>();
  readonly audit = input.required<Audit>();
  readonly organizations = input<Organization[]>();

  dialog = inject(MatDialog);
  currentDate = new Date();
  plants!: Plant[];
  unsubscribeService = inject(UnsubscribeService);

  allPlants!: Plant[];

  ngOnInit() {
    this.dateFormat();
    this.loadPlants();
  }

  dateFormat() {
    this._adapter.setLocale('de');
  }

  isEditable() {
    return this.form()?.enable;
  }

  isDraft() {
    return this.form()?.value.status === 'Draft';
  }

  isAudit(audit: string): boolean {
    return this.form()?.value.auditor === audit;
  }

  isGPIA(): boolean {
    return this.form()?.value.auditor === Auditor.GPIA;
  }

  isOnlyOnePlant(): boolean {
    return this.plants.length === 1;
  }

  isStatus(status: string): boolean {
    return status === this.form()?.get('status')?.value;
  }

  loadPlants(): void {
    this.plantService.getPlants().subscribe({
      next: (plants) => {
        this.plants = plants;
        this.allPlants = plants;
        this.plants.sort(this.sortObjects);
      }
    });
  }

  changePlant() {
    const selectedPlant = this.plants.filter((plant: Plant) => {
      return plant.plantName === this.form()?.get('plant')?.value;
    });
    if (selectedPlant && selectedPlant[0]) {
      const plant = selectedPlant[0];
      this.form()?.get('legalUnit')?.setValue(plant.legalUnit);
      this.setCompanyData();
    }
  }

  showEditableDraft() {
    return this.isEditable() && this.isDraft();
  }

  showBG(): boolean {
    return true;
  }

  setCompanyData(): void {
    const legalUnit = this.form().get('legalUnit')?.value;
    if (legalUnit) {
      this.plants = this.allPlants.filter((plant: Plant) => {
        return !legalUnit || plant.legalUnit === legalUnit;
      });
    }

    if (this.plants != null && this.plants.length === 1) {
      this.form().get('plant')?.setValue(this.plants[0].plantName);
    }
    const filteredOrgs = this.organizations()?.filter((organization) => {
      return organization.legalUnitCode === legalUnit;
    });

    if (filteredOrgs && filteredOrgs!.length > 0) {
      const organization = filteredOrgs[0];
      this.checkCompanyField(organization.businessGroupCode, 'department', false);
      this.checkCompanyField(organization.legalUnitName, 'concernsName', false);
    }

    if (this.isOnlyOnePlant()) {
      this.form().controls['plant'].setValue(this.plants[0].plantName);
    }
  }

  checkCompanyField(organizationValue: string, formName: string, arr: boolean) {
    if (organizationValue) {
      this.form()
        ?.get(formName)
        ?.setValue(arr ? [organizationValue] : organizationValue);
    }
  }

  sortObjects(a: Plant, b: Plant): number {
    const plantNameA = a.plantName.toUpperCase();
    const plantNameB = b.plantName.toUpperCase();

    let comparison = 0;
    if (plantNameA > plantNameB) {
      comparison = 1;
    } else if (plantNameA < plantNameB) {
      comparison = -1;
    }
    return comparison;
  }

  setNewStatus(status: string) {
    this.form().controls['status'].setValue(status);
    const form = this.form();
    const auditId = form?.get('id')?.value;
    const newStatus = form?.get('status')?.value;
    const auditor = form?.get('auditor')?.value;
    const department = form?.get('department')?.value;
    const assessmentCoordinator = form?.get('assessmentCoordinator')?.value;

    this.auditService.setStatus(auditId, newStatus, auditor, department, assessmentCoordinator).subscribe({
      next: () => {
        this.snackBar.open('Save successful!', undefined, {
          duration: 2000
        });
        this.router.navigate(['/audits/', auditor]);
      },
      error: () => {
        this.snackBar.open('Something went wrong contact the IT!', undefined, {
          panelClass: 'error',
          duration: 2000
        });
      }
    });
  }

  selectPerson(fieldName: string): void {
    this.dialog
      .open(PersonSelectComponent, {
        data: {
          currentPeople: [this.form().get(fieldName)?.value],
          singleSelect: true
        }
      })
      .afterClosed()
      .pipe(this.unsubscribeService.takeUntilDestroy)
      .subscribe((data: { save: boolean; id: string }) => {
        if (!data?.save) return;
        this.form().get(fieldName)?.setValue(data.id);
      });
  }
  uniqueOrgs(): string[] {
    const orgs = this.organizations() || [];
    const codes = orgs
      .map((org) => org.legalUnitCode)
      .filter((code) => code)
      .sort();
    return [...new Set(codes)];
  }

  chkError(formName: string): boolean {
    const field = this.form()?.controls[formName];
    return !!(field && field.errors && field.touched);
  }
}
